import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  ShoppingCart,
  Package,
  Loader2,
  Store,
  ShoppingBag,
  Plus,
  Minus,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getCourier } from '../../state/courierSlice';
import { apiConfig } from '../../shared/api-config';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { useCart } from '../../context/CartContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { ShopProductApi, ShopProduct as ShopProductModel, ShopProductOption, ShopProductOptionChoice } from '@shared/sdk-v1';
import { useTrackEvent } from '../../utils/track';

const ShopBrowse: React.FC = () => {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const courier = useSelector(getCourier);
  const track = useTrackEvent();
  const { cartItems, addToCart, updateQuantity, removeFromCart } = useCart();

  // State
  const [products, setProducts] = useState<ShopProductModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ShopProductModel | null>(null);

  // Navigate to product detail page
  const openProductDetail = (product: ShopProductModel) => {
    navigate(`/shop/product/${product.id}`);
  };

  // Fire a debounced `search` event once the user stops typing (never per keystroke).
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) return;
    const timeout = setTimeout(() => {
      track('search', { search_term: term, search_context: 'shop' });
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm, track]);

  const selectCategory = (category: string) => {
    if (category !== 'all') {
      track('filter_applied', {
        filter_type: 'category',
        filter_value: category,
        context: 'shop',
      });
    }
    setSelectedCategory(category);
  };

  // Get unique categories
  const categories = ['all', ...new Set(products.map(p => p.category).filter(Boolean) as string[])];

  // Filtered products
  const filteredProducts = products.filter(product => {
    const matchesSearch = 
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = 
      selectedCategory === 'all' || product.category === selectedCategory;
    
    return matchesSearch && matchesCategory && product.status === 'active';
  });

  // Fetch products
  const fetchProducts = useCallback(async () => {
    if (!courier?.id) return;

    setIsLoading(true);
    try {
      const api = new ShopProductApi(apiConfig());
      const data = await api.shopProductFind({
        filter: JSON.stringify({
          where: { courierId: courier.id, status: 'active' },
          order: 'createdAt DESC',
          include: [{ relation: 'options', scope: { include: ['choices'], order: 'sortOrder ASC' } }],
        })
      });
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      showErrorToast({ title: 'Error', message: 'Failed to load products' });
    } finally {
      setIsLoading(false);
    }
  }, [courier?.id]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Get condition badge color
  const getConditionBadge = (condition?: string) => {
    const baseClasses = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';
    
    switch (condition?.toLowerCase()) {
      case 'new':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'like_new':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'good':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'fair':
        return `${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  // State for configurable product options
  const [selectedOptions, setSelectedOptions] = useState<Record<number, string[]>>({});

  // Handle add to cart (simple products)
  const handleAddToCart = (product: ShopProductModel) => {
    if (!product.id || !product.name || !product.price) return;

    const existingItem = cartItems.find(item => item.cartKey === String(product.id));

    if (existingItem) {
      updateQuantity(existingItem.cartKey, existingItem.quantity + 1);
    } else {
      addToCart({
        productId: product.id,
        name: product.name,
        price: product.price,
        basePrice: product.price,
        image: product.images?.[0],
        condition: product.condition
      });
    }

    showSuccessToast({ title: 'Added to cart', message: product.name || '' });
  };

  // Handle add configurable product to cart
  const handleAddConfigurableToCart = (product: ShopProductModel) => {
    if (!product.id || !product.name || !product.price || !product.options) return;

    // Validate required options
    for (const opt of product.options) {
      if (opt.required && (!selectedOptions[opt.id!] || selectedOptions[opt.id!].length === 0)) {
        showErrorToast({ title: 'Required', message: `Please select ${opt.name}` });
        return;
      }
    }

    // Build selected options with price modifiers
    const opts: import('../../context/CartContext').SelectedOption[] = [];
    let totalModifier = 0;

    for (const opt of product.options) {
      const choiceIds = selectedOptions[opt.id!] || [];
      if (choiceIds.length === 0) continue;
      
      const choiceNames: string[] = [];
      let optModifier = 0;
      for (const chId of choiceIds) {
        const choice = opt.choices?.find(c => String(c.id) === chId);
        if (choice) {
          choiceNames.push(choice.name || '');
          optModifier += choice.priceModifier || 0;
        }
      }
      totalModifier += optModifier;
      opts.push({ optionName: opt.name || '', choiceNames, priceModifier: optModifier });
    }

    const finalPrice = (product.price || 0) + totalModifier;
    const optsSummary = opts.map(o => o.choiceNames.join(', ')).join(' · ');

    addToCart({
      productId: product.id,
      name: `${product.name} (${optsSummary})`,
      price: finalPrice,
      basePrice: product.price,
      image: product.images?.[0],
      condition: product.condition,
      selectedOptions: opts,
    });

    showSuccessToast({ title: 'Added to cart', message: product.name || '' });
    setSelectedProduct(null);
    setSelectedOptions({});
  };

  // Get product quantity in cart (simple products only)
  const getProductQuantity = (productId: number) => {
    const item = cartItems.find(item => item.cartKey === String(productId));
    return item?.quantity || 0;
  };

  // Product Detail Modal
  const renderProductDetailModal = () => {
    if (!selectedProduct) return null;

    return (
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
        onClick={() => setSelectedProduct(null)}
      >
        <div 
          className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 p-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Product Details</h2>
            <button
              onClick={() => setSelectedProduct(null)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-700 dark:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Images */}
            {selectedProduct.images && selectedProduct.images.length > 0 ? (
              <div className="aspect-[4/3] max-h-56 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                <img
                  src={selectedProduct.images[0].startsWith('http') ? selectedProduct.images[0] : `${import.meta.env.VITE_API_BASE_URL}/Uploads/${selectedProduct.courierId}/download/${selectedProduct.images[0]}`}
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="30" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>
            ) : (
              <div className="aspect-[4/3] max-h-56 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <ShoppingBag className="h-16 w-16 text-gray-400 dark:text-gray-500" />
              </div>
            )}

            {/* Info */}
            <div className="space-y-3">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedProduct.name}</h3>
                {selectedProduct.condition && (
                  <span className={`${getConditionBadge(selectedProduct.condition)} mt-2`}>
                    {selectedProduct.condition.replace('_', ' ')}
                  </span>
                )}
              </div>

              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  JMD ${selectedProduct.price?.toFixed(2) || '0.00'}
                </span>
                {selectedProduct.originalPrice && selectedProduct.originalPrice > (selectedProduct.price || 0) && (
                  <span className="text-lg text-gray-500 dark:text-gray-400 line-through">
                    ${selectedProduct.originalPrice.toFixed(2)}
                  </span>
                )}
              </div>

              {selectedProduct.description && (
                <div className="pt-4 border-t dark:border-gray-700">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Description</h4>
                  <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                    {selectedProduct.description}
                  </p>
                </div>
              )}

              {selectedProduct.category && (
                <div className="pt-4 border-t dark:border-gray-700">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Category</h4>
                  <p className="text-gray-600 dark:text-gray-300">{selectedProduct.category}</p>
                </div>
              )}

              {/* Configurable Options */}
              {selectedProduct.isConfigurable && selectedProduct.options && selectedProduct.options.length > 0 && (
                <div className="pt-4 border-t dark:border-gray-700 space-y-4">
                  {selectedProduct.options.map((opt) => (
                    <div key={opt.id}>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                        {opt.name}
                        {opt.required && <span className="text-red-500 ml-1">*</span>}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {opt.choices?.map((choice) => {
                          const isSelected = (selectedOptions[opt.id!] || []).includes(String(choice.id));
                          return (
                            <button
                              key={choice.id}
                              onClick={() => {
                                setSelectedOptions(prev => {
                                  const current = prev[opt.id!] || [];
                                  if (opt.type === 'single') {
                                    return { ...prev, [opt.id!]: isSelected ? [] : [String(choice.id)] };
                                  } else {
                                    return {
                                      ...prev,
                                      [opt.id!]: isSelected
                                        ? current.filter(id => id !== String(choice.id))
                                        : [...current, String(choice.id)]
                                    };
                                  }
                                });
                              }}
                              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors min-h-[44px]
                                ${isSelected
                                  ? 'bg-primary text-white border-primary'
                                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-primary'
                                }`}
                            >
                              {choice.name}
                              {(choice.priceModifier || 0) > 0 && (
                                <span className="ml-1 opacity-75">+${choice.priceModifier?.toFixed(2)}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {opt.type === 'multi' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Select one or more</p>
                      )}
                    </div>
                  ))}

                  {/* Price with modifiers */}
                  {(() => {
                    let totalMod = 0;
                    for (const opt of selectedProduct.options || []) {
                      for (const chId of (selectedOptions[opt.id!] || [])) {
                        const ch = opt.choices?.find(c => String(c.id) === chId);
                        totalMod += ch?.priceModifier || 0;
                      }
                    }
                    return totalMod > 0 ? (
                      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mt-2">
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>Base price</span>
                          <span>JMD ${selectedProduct.price?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>Options</span>
                          <span>+${totalMod.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-gray-900 dark:text-white border-t dark:border-gray-700 mt-2 pt-2">
                          <span>Total</span>
                          <span>JMD ${((selectedProduct.price || 0) + totalMod).toFixed(2)}</span>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              <div className="pt-4 border-t dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {selectedProduct.quantity && selectedProduct.quantity > 0 
                    ? `${selectedProduct.quantity} available` 
                    : 'Out of stock'}
                </p>
              </div>
            </div>

            {/* Add to Cart Button */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 pt-4">
              {selectedProduct.quantity && selectedProduct.quantity > 0 ? (
                <button
                  onClick={() => {
                    if (selectedProduct.isConfigurable && selectedProduct.options?.length) {
                      handleAddConfigurableToCart(selectedProduct);
                    } else {
                      handleAddToCart(selectedProduct);
                      setSelectedProduct(null);
                    }
                  }}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-lg 
                    font-medium text-lg flex items-center justify-center min-h-[44px]"
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  Add to Cart
                </button>
              ) : (
                <button
                  disabled
                  className="w-full bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 
                    py-4 rounded-lg font-medium text-lg cursor-not-allowed min-h-[44px]"
                >
                  Sold Out
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900">
      {/* Desktop Hero Section */}
      <div className="hidden lg:block bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Welcome to {courier?.name || 'our shop'}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400 text-lg">
            Browse our collection and find something you love
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 lg:px-6 lg:py-8">
        {/* Desktop Search Bar */}
        <div className="hidden lg:flex justify-center mb-8">
          <div className="relative w-full max-w-xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-5 w-5" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl 
                bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500
                focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm text-base"
            />
          </div>
        </div>

        {/* Mobile Search and Filter */}
        <div className="lg:hidden flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg 
                bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 min-h-[44px]
                focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="relative">
            <button
              onClick={() => setShowCategoryFilter(!showCategoryFilter)}
              className="inline-flex items-center px-4 py-2.5 border border-gray-300 dark:border-gray-600 
                rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200
                hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
            >
              <Filter className="h-4 w-4 mr-2" />
              <span className="whitespace-nowrap">
                {selectedCategory === 'all' ? 'All Categories' : selectedCategory}
              </span>
            </button>

            {showCategoryFilter && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowCategoryFilter(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg 
                  border border-gray-300 dark:border-gray-600 z-20">
                  <div className="py-1">
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => {
                          selectCategory(category);
                          setShowCategoryFilter(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 min-h-[44px]
                          ${selectedCategory === category ? 'bg-blue-50 dark:bg-blue-900/20 text-primary dark:text-blue-400' : 
                            'text-gray-900 dark:text-white'}`}
                      >
                        {category === 'all' ? 'All Categories' : category}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Desktop: Sidebar + Grid layout / Mobile: Grid only */}
        <div className="lg:flex lg:gap-8">
          {/* Desktop Sidebar Categories */}
          {categories.length > 2 && (
            <aside className="hidden lg:block w-56 flex-shrink-0">
              <div className="sticky top-4">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Categories
                </h3>
                <nav className="space-y-1">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => selectCategory(category)}
                      className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px]
                        ${selectedCategory === category
                          ? 'bg-primary/10 text-primary dark:text-blue-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                    >
                      {category === 'all' ? 'All Categories' : category}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>
          )}

          {/* Products Area */}
          <div className="flex-1 min-w-0">
            {/* Product count */}
            {!isLoading && filteredProducts.length > 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
              </p>
            )}

            {/* Products Grid */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary dark:text-blue-400" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Store className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No products found</h3>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  {searchTerm || selectedCategory !== 'all' 
                    ? 'Try adjusting your search or filters' 
                    : 'Check back later for new products'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {filteredProducts.map((product) => {
                  const quantity = getProductQuantity(product.id!);
                  
                  return (
                    <div 
                      key={product.id} 
                      className="group bg-white dark:bg-gray-800 rounded-lg lg:rounded-xl shadow-sm overflow-hidden 
                        hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-700
                        lg:hover:-translate-y-1"
                    >
                      {/* Product Image */}
                      <div 
                        className="aspect-[4/5] bg-gray-100 dark:bg-gray-700 relative cursor-pointer overflow-hidden"
                        onClick={() => openProductDetail(product)}
                      >
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={product.images[0].startsWith('http') ? product.images[0] : `${import.meta.env.VITE_API_BASE_URL}/Uploads/${product.courierId}/download/${product.images[0]}`}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="20" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ShoppingBag className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                        
                        {product.condition && (
                          <span className={`absolute top-2 right-2 ${getConditionBadge(product.condition)}`}>
                            {product.condition.replace('_', ' ')}
                          </span>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="p-3 lg:p-4 space-y-2">
                        <h3 
                          className="font-medium text-gray-900 dark:text-white truncate cursor-pointer lg:text-base" 
                          title={product.name}
                          onClick={() => openProductDetail(product)}
                        >
                          {product.name}
                        </h3>
                        
                        {product.category && (
                          <p className="hidden lg:block text-xs text-gray-500 dark:text-gray-400 truncate">
                            {product.category}
                          </p>
                        )}

                        <div className="space-y-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-bold text-gray-900 dark:text-white">
                              JMD ${product.price?.toFixed(2) || '0.00'}
                            </span>
                            {product.originalPrice && product.originalPrice > (product.price || 0) && (
                              <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
                                ${product.originalPrice.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Add to Cart Button or Quantity Control */}
                        {product.quantity && product.quantity > 0 ? (
                          quantity > 0 ? (
                            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                              <button
                                onClick={() => {
                                  if (quantity === 1) {
                                    removeFromCart(String(product.id!));
                                  } else {
                                    updateQuantity(String(product.id!), quantity - 1);
                                  }
                                }}
                                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-700 dark:text-gray-200"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <span className="flex-1 text-center font-medium text-gray-900 dark:text-white">
                                {quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(String(product.id!), quantity + 1)}
                                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-700 dark:text-gray-200"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                          ) : product.isConfigurable ? (
                            <button
                              onClick={() => openProductDetail(product)}
                              className="w-full bg-primary hover:bg-primary/90 text-white py-2 rounded-lg 
                                font-medium text-sm flex items-center justify-center min-h-[44px]"
                            >
                              Choose Options
                            </button>
                          ) : (
                            <button
                              onClick={() => handleAddToCart(product)}
                              className="w-full bg-primary hover:bg-primary/90 text-white py-2 rounded-lg 
                                font-medium text-sm flex items-center justify-center min-h-[44px]"
                            >
                              <ShoppingCart className="h-4 w-4 mr-1" />
                              Add to Cart
                            </button>
                          )
                        ) : (
                          <button
                            disabled
                            className="w-full bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 
                              py-2 rounded-lg font-medium text-sm cursor-not-allowed min-h-[44px]"
                          >
                            Sold Out
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && renderProductDetailModal()}
    </div>
  );
};

export default ShopBrowse;
